import type {
  BodyType,
  DataType,
  KeyType,
  KeyValue,
  RuleHelperBody,
  RuleInputBody,
  RuleTest,
  ScriptKeysLookup,
  StringDict,
  UsedKeys,
  UsedValues,
} from '@/types'
import omit from 'lodash/omit'
import pick from 'lodash/pick'
import { resultOptions, type ResultOptionKey } from './testExpectationUtils'

/** These fields must be set in any policy unit test */
export const requiredPolicyTestFields = ['cid', 'persona_id']

const safeJsonParse = (val: string) => {
  try {
    return JSON.parse(val)
  } catch (e) {
    return undefined
  }
}

const safeParseInt = (val: string) => {
  const num = parseInt(val, 10)
  if (isNaN(num)) {
    return undefined
  }

  return num
}

const safeParseFloat = (val: string) => {
  const num = parseFloat(val)
  if (isNaN(num)) {
    return undefined
  }

  return num
}

const noOpConversion = <T>(item: T | null): T | null => item
const fallbackToUndefined = <T>(val: T | null) => (val === null ? null : undefined)

const strConverters: { [key in DataType]: (val: string | null) => any } = {
  str: noOpConversion,
  int: (val: string | null) => (val === null ? null : safeParseInt(val)),
  float: (val: string | null) => (val === null ? null : safeParseFloat(val)),
  // In some instances, rules containing "bool" data types would pass in `val` as an actual boolean
  // so we coerce the value to a `String` before applying the transformation
  bool: (val: any) => (val === null ? null : String(val).trim().toLowerCase() === 'true'),
  dict: (val: string | null) => (val === null ? null : safeJsonParse(val)),
  list: (val: string | null) => (val === null ? null : safeJsonParse(val)),
}

const boolConverters: { [key in DataType]: (val: boolean | null) => any } = {
  str: (val: boolean | null) => (val === null ? null : `${val}`),
  int: (val: boolean | null) => (val === null ? null : val ? 1 : 0),
  float: (val: boolean | null) => (val === null ? null : val ? 1 : 0),
  bool: noOpConversion,
  dict: fallbackToUndefined,
  list: fallbackToUndefined,
}

const numberConverters: {
  [key in DataType]: (num: number | null) => any
} = {
  str: (val: number | null) => (val === null ? null : `${val}`),
  int: noOpConversion,
  float: noOpConversion,
  bool: (val: number | null) => (val === null ? null : !!val),
  dict: fallbackToUndefined,
  list: fallbackToUndefined,
}

const intConverters = numberConverters

const floatConverters = {
  ...numberConverters,
  int: (val: number | null) => (val === null ? null : Math.floor(val)),
}

const dictConverters: {
  [key in DataType]: (val: object | null) => any
} = {
  str: fallbackToUndefined,
  int: fallbackToUndefined,
  float: fallbackToUndefined,
  bool: fallbackToUndefined,
  dict: noOpConversion,
  list: fallbackToUndefined,
}

const listConverters: {
  [key in DataType]: (val: Array<any> | null) => any
} = {
  str: fallbackToUndefined,
  int: fallbackToUndefined,
  float: fallbackToUndefined,
  bool: fallbackToUndefined,
  dict: fallbackToUndefined,
  list: fallbackToUndefined,
}

const converters: {
  [key in DataType]: { [key in DataType]: (val: any) => any }
} = {
  str: strConverters,
  bool: boolConverters,
  int: intConverters,
  float: floatConverters,
  dict: dictConverters,
  list: listConverters,
}

export const convertValue = (fromType: DataType, toType: DataType, value: any) => {
  if (value === null) {
    return null
  }

  if (value === undefined) {
    return undefined
  }

  return converters[fromType][toType](value)
}

/**
 * Gets the helper or input "body"
 *
 * @param keyType "input" | "helper"
 * @param scriptKeysLookup State containing { inputs, functions } data
 * @param key The name of the helper or input
 *
 * @throws Error if no such key exists
 */
export function getBody<T extends KeyType>(keyType: T, scriptKeysLookup: ScriptKeysLookup, key: string): BodyType<T> {
  const body = getMaybeBody(keyType, scriptKeysLookup, key)
  if (!body) {
    throw new Error(`Non-existent ${keyType} named "${key}" referenced. Please update the rules.`)
  }

  return body
}

/**
 * Gets the helper or input "body", returns undefined it doesn't exist
 *
 * @param keyType "input" | "helper"
 * @param scriptKeysLookup State containing { inputs, functions } data
 * @param key The name of the helper or input
 */
export function getMaybeBody<T extends KeyType>(
  keyType: T,
  scriptKeysLookup: ScriptKeysLookup,
  key: string
): BodyType<T> {
  return (keyType === 'input' ? scriptKeysLookup.inputs[key] : scriptKeysLookup.helpers[key]) as BodyType<T>
}

/**
 * Converts the string representation of a value to its native data type.
 * In case of null it returns null.
 *
 * @param str The string representation of a value
 * @param dataType The data type the is supposed to be.
 */
export const convertStrToNativeDatatype = (str: string | null | undefined, dataType: DataType) => {
  if (typeof str === 'undefined') {
    return undefined
  }

  return strConverters[dataType](str)
}

export const convertStrToNativeTypeNoUndefined = (
  str: string | null | undefined,
  dataType: DataType,
  defaultValue?: any
) => {
  if (typeof str === 'undefined') {
    return defaultValue || defaultValueForDataType[dataType]
  }

  return strConverters[dataType](str)
}

export function getDefaultValue(scriptKeysLookup: ScriptKeysLookup, key: string, keyType: KeyType): any {
  const body = getBody(keyType, scriptKeysLookup, key)

  if (keyType === 'input') {
    const input = body as RuleInputBody
    return input.example
  }

  const helper = getBody(keyType, scriptKeysLookup, key)

  // If the helper sample value is of the same data type as the expected return type,
  // use it to populate the initial run values — otherwise, use a default value for the data type
  return isOfDataType(helper.sample_value, helper.return_type)
    ? helper.sample_value
    : getDefaultValueForDataType(helper.return_type)
}

export const getDefaultValues = (scriptKeysLookup: ScriptKeysLookup, keys: string[], keyType: KeyType): StringDict =>
  keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: getDefaultValue(scriptKeysLookup, key, keyType),
    }),
    {}
  )

export const getDefaultValuesUsingProvider = (
  defaultValueProvider: (keyType: KeyType, keyName: string) => any,
  keys: string[],
  keyType: KeyType
): StringDict =>
  keys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: defaultValueProvider(keyType, key),
    }),
    {}
  )

/**
 * Converts a native value type to its string representation
 *
 * @param dataType
 * @param value
 */
export const valueAsString = (value: any, dataType: DataType) => {
  const isJson = ['dict', 'list'].includes(dataType)
  const isBool = dataType === 'bool'

  if (isJson) {
    return JSON.stringify(value, null, 2)
  }

  if (isBool) {
    return value === true ? 'True' : 'False'
  }

  return String(value)
}

/**
 * Gets the helper or input data type
 *
 * @param keyType "input" | "helper"
 * @param scriptKeysLookup State containing { inputs, helpers } data
 * @param key The name of the helper or input
 *
 * @throws Error if no such key exists
 */
export const getDataType = (keyType: KeyType, scriptKeysLookup: ScriptKeysLookup, key: string): DataType => {
  const body = getBody(keyType, scriptKeysLookup, key)

  if (keyType === 'helper') {
    return (body as RuleHelperBody).return_type || (body as RuleInputBody).type
  }

  return (body as RuleInputBody).type
}

/**
 * Check if the native type of `value` is of the given data type.
 *
 * @param value The value to check.
 * @param dataType The data type to check against.
 * @returns Whether the value is of the given data type.
 */
export function isOfDataType(value: any, dataType: DataType): boolean {
  switch (dataType) {
    case 'str':
      return typeof value === 'string'
    case 'int':
      return Number.isInteger(value)
    case 'float':
      return typeof value === 'number'
    case 'bool':
      return typeof value === 'boolean'
    case 'dict':
      return typeof value === 'object' && !Array.isArray(value)
    case 'list':
      return Array.isArray(value)
    default:
      return false
  }
}

/**
 * Returns the string representation of the value of a `key` in a test.
 * This value is for UI inputs.
 *
 * If it is `undefined` in the test it will fall back to a default value.
 * Note if it is `null` it will return null. This is an explicit value we want to allow.
 *
 * @param scriptKeysLookup the inputs and functions (helpers) metadata
 * @param testItem The test (Policy or Rule test) or usedValues
 * @param key The name of the input field
 * @param keyType "input" or "helper"
 */
export const getTestValueAsString = <T extends UsedValues>(
  scriptKeysLookup: ScriptKeysLookup,
  testItem: T,
  key: string,
  keyType: KeyType
) => {
  const value = keyType === 'input' ? testItem.inputs[key] : testItem.helpers[key]
  const dataType: DataType = getDataType(keyType, scriptKeysLookup, key)
  const defaultValue = getDefaultValue(scriptKeysLookup, key, keyType)

  return testValueAsStringOrNull(value, dataType, defaultValue)
}

const defaultValueForDataType: { [key in DataType]: any } = {
  str: '',
  int: 0,
  float: 0,
  bool: false,
  dict: {},
  list: [],
}

export const getDefaultValueForDataType = (dataType: DataType) => defaultValueForDataType[dataType]

export const testValueAsStringOrNull = (value: any, dataType: DataType, defaultValue?: any): string | null => {
  if (typeof value === 'undefined') {
    return valueAsString(defaultValue || defaultValueForDataType[dataType], dataType)
  }

  return value === null ? null : valueAsString(value, dataType)
}

/**
 * Legacy tests have their values encoded as strings instead of native json types.
 * Can be removed once all format_version < 1 rules & policies are gone.
 */
export const testWithNativeValueTypes = <T extends UsedValues>(
  scriptKeysLookup: ScriptKeysLookup,
  test: T,
  inputsWhitelist?: string[],
  helpersWhitelist?: string[]
) => {
  // If no whitelists are given, fallback to test input/helper keys as whitelist (basically whitelisting itself)
  inputsWhitelist = inputsWhitelist || Object.keys(test.inputs)
  helpersWhitelist = helpersWhitelist || Object.keys(test.helpers)

  const whitelistedInputs = pick(test.inputs, inputsWhitelist)
  const whitelistedHelpers = pick(test.helpers, helpersWhitelist)

  const defaultInputValues = getDefaultValues(scriptKeysLookup, inputsWhitelist, 'input')
  const defaultHelperValues = getDefaultValues(scriptKeysLookup, helpersWhitelist, 'helper')

  const inputsAsNativeTypes = Object.keys(whitelistedInputs).reduce(
    (acc, key) => ({
      ...acc,
      [key]: convertStrToNativeDatatype(test.inputs[key], scriptKeysLookup.inputs[key].type),
    }),
    {} as any
  )

  // Remove properties with `undefined` value.
  Object.keys(inputsAsNativeTypes).forEach(
    key => inputsAsNativeTypes[key] === undefined && delete inputsAsNativeTypes[key]
  )

  const helpersAsNativeTypes = Object.keys(whitelistedHelpers).reduce(
    (acc, key) => ({
      ...acc,
      [key]: convertStrToNativeDatatype(whitelistedHelpers[key], scriptKeysLookup.helpers[key].return_type),
    }),
    {} as any
  )

  // Remove properties with `undefined` value.
  Object.keys(helpersAsNativeTypes).forEach(
    key => helpersAsNativeTypes[key] === undefined && delete helpersAsNativeTypes[key]
  )

  return {
    ...test,
    inputs: Object.assign({}, defaultInputValues, inputsAsNativeTypes),
    helpers: Object.assign({}, defaultHelperValues, helpersAsNativeTypes),
  }
}

export const sanitizeTests = <T extends UsedValues>(
  scriptKeysLookup: ScriptKeysLookup,
  tests: T[],
  usedKeys: UsedKeys
) => tests.map(test => sanitizeTest(scriptKeysLookup, test, usedKeys))

/**
 * Sanitizes away any legacy values from test values, makes sure no standalone helpers are included.
 */
export const sanitizeTest = <T extends UsedValues>(scriptKeysLookup: ScriptKeysLookup, test: T, usedKeys: UsedKeys) =>
  testWithWhitelistedAndDefaultValues(
    scriptKeysLookup,
    test,
    usedKeys.inputs,
    withoutStandaloneHelpersWithoutValue(scriptKeysLookup, usedKeys.helpers, test)
  )

export const sanitizeTestsWithValueProvider = <T extends UsedValues>(
  scriptKeysLookup: ScriptKeysLookup,
  defaultValueProvider: (keyType: KeyType, keyName: string) => any,
  tests: T[],
  usedKeys: UsedKeys
) => tests.map(test => sanitizeTestWithValueProvider(scriptKeysLookup, defaultValueProvider, test, usedKeys))

/**
 * Sanitizes away any legacy values from test values, makes sure no standalone helpers are included.
 */
export const sanitizeTestWithValueProvider = <T extends UsedValues>(
  scriptKeysLookup: ScriptKeysLookup,
  defaultValueProvider: (keyType: KeyType, keyName: string) => any,
  test: T,
  usedKeys: UsedKeys
) =>
  testWithWhitelistedAndDefaultValuesUsingProvider(
    defaultValueProvider,
    test,
    usedKeys.inputs,
    withoutStandaloneHelpersWithoutValue(scriptKeysLookup, usedKeys.helpers, test)
  )

/**
 * Makes sure a test only has the values defined in script (whitelisting),
 * and has defaults set for `undefined` values. This is important for the console & test runs.
 */
export const testWithWhitelistedAndDefaultValues = <T extends UsedValues>(
  scriptKeysLookup: ScriptKeysLookup,
  test: T,
  inputsWhitelist?: string[],
  helpersWhitelist?: string[]
) => {
  const defaultValueProvider = (keyType: KeyType, keyName: string) => {
    return getDefaultValue(scriptKeysLookup, keyName, keyType)
  }

  return testWithWhitelistedAndDefaultValuesUsingProvider(defaultValueProvider, test, inputsWhitelist, helpersWhitelist)
}

/**
 * Makes sure a test only has the values defined in script (whitelisting),
 * and has defaults set for `undefined` values. This is important for the console & test runs.
 */
export const testWithWhitelistedAndDefaultValuesUsingProvider = <T extends UsedValues>(
  defaultValueProvider: (keyType: KeyType, keyName: string) => any,
  test: T,
  inputsWhitelist?: string[],
  helpersWhitelist?: string[]
) => {
  // If no whitelists are given, fallback to test input/helper keys as whitelist (basically whitelisting itself)
  inputsWhitelist = inputsWhitelist || Object.keys(test.inputs)
  helpersWhitelist = helpersWhitelist || Object.keys(test.helpers)

  const whitelistedInputs = pick(test.inputs, inputsWhitelist)
  const whitelistedHelpers = pick(test.helpers, helpersWhitelist)

  const defaultInputValues = getDefaultValuesUsingProvider(defaultValueProvider, inputsWhitelist, 'input')
  const defaultHelperValues = getDefaultValuesUsingProvider(defaultValueProvider, helpersWhitelist, 'helper')

  // Remove properties with `undefined` value.
  Object.keys(whitelistedInputs).forEach(key => whitelistedInputs[key] === undefined && delete whitelistedInputs[key])

  // Remove properties with `undefined` value.
  Object.keys(whitelistedHelpers).forEach(
    key => whitelistedHelpers[key] === undefined && delete whitelistedHelpers[key]
  )

  return {
    ...test,
    inputs: Object.assign({}, defaultInputValues, whitelistedInputs),
    helpers: Object.assign({}, defaultHelperValues, whitelistedHelpers),
  }
}

/**
 * Convenience function to help us update a single test input value given as a string
 * and automatically convert it to the correct data type
 * @param values UsedValues (or RuleTest/PolicyTest)
 * @param key Name of input
 * @param value String value of input
 */
export const updateInputValue = <T extends UsedValues>(
  scriptKeysLookup: ScriptKeysLookup,
  values: T,
  key: string,
  value: string
) => ({
  ...values,
  inputs: {
    ...values.inputs,
    [key]: convertStrToNativeDatatype(value, scriptKeysLookup.inputs[key].type),
  },
})

export const updateUserFunctionInputValue = <T extends UsedValues>(
  values: T,
  key: string,
  dataType: DataType,
  value: string
) => ({
  ...values,
  inputs: {
    ...values.inputs,
    [key]: convertStrToNativeDatatype(value, dataType),
  },
})

/**
 * Convenience function to help us update a single test helper value given as a string
 * and automatically convert it to the correct data type
 * @param values UsedValues (or RuleTest/PolicyTest)
 * @param key Name of helper
 * @param value String value of helepr
 */
export const updateHelperValue = <T extends UsedValues>(
  scriptKeysLookup: ScriptKeysLookup,
  values: T,
  key: string,
  value: string
) => {
  if (value === undefined) {
    return {
      ...values,
      helpers: omit(values.helpers, key),
    }
  }
  return {
    ...values,
    helpers: {
      ...values.helpers,
      [key]: convertStrToNativeDatatype(
        value,
        scriptKeysLookup.helpers[key].return_type || (scriptKeysLookup.helpers[key] as any).type
      ),
    },
  }
}
interface TestSummary {
  prefix: string
  expectations: string[]
  withExpectations: boolean
  keyValues: KeyValue[]
}

/**
 * Returns the test summary data structure
 */
export const buildSummaryData = (
  scriptKeysLookup: ScriptKeysLookup,
  testItem: RuleTest,
  { inputs, helpers }: UsedKeys
): TestSummary => {
  const prefix = (testItem.result ? `${testItem.result.decision.toUpperCase()}` : 'SKIPPED') as ResultOptionKey

  const inputEntries = inputs.map(key => ({
    key,
    value: getTestValueAsString(scriptKeysLookup, testItem, key, 'input'),
  }))

  const helperEntries = helpers.map(key => ({
    key,
    value: getTestValueAsString(scriptKeysLookup, testItem, key, 'helper'),
  }))

  const compareKeys = (a: { key: string }, b: { key: string }) => a.key.localeCompare(b.key)
  inputEntries.sort(compareKeys)
  helperEntries.sort(compareKeys)

  const allEntries = [...inputEntries, ...helperEntries]

  const includedHelperAndInputEntries = allEntries.filter(({ key }) => (testItem.summaryHelpers || []).includes(key))

  const expectations = ((testItem.result! || {}).expectations || []).map(e => {
    const option = resultOptions[prefix].find(o => o.name === e.name)!
    let result = option.template
    if (option.valueOptions) {
      const valueOption = option.valueOptions.find(vo => vo.value === e.value)!
      result = result.replace('{valueLabel}', valueOption.label.toLowerCase())
    }
    return result.replace('{value}', e.value)
  })

  return {
    prefix,
    expectations,
    withExpectations: expectations.length > 0,
    keyValues: includedHelperAndInputEntries,
  }
}

export const buildUsedKeyMetasWithDefaults = (scriptKeysLookup: ScriptKeysLookup, usedKeys: UsedKeys) => ({
  inputs: buildInputKeysMeta(scriptKeysLookup, usedKeys.inputs),
  helpers: buildHelperKeysMeta(scriptKeysLookup, usedKeys.helpers),
})

export const buildInputKeysMeta = (scriptKeysLookup: ScriptKeysLookup, keyNames: string[]) =>
  keyNames
    .map(keyName => ({
      name: keyName,
      type: getDataType('input', scriptKeysLookup, keyName),
      defaultValue: getDefaultValue(scriptKeysLookup, keyName, 'input'),
      initiallyMockable: true,
    }))
    .sort((first, second) => first.name.localeCompare(second.name))

export const isMockableHelper = (scriptKeysLookup: ScriptKeysLookup, keyName: string) =>
  !(getBody('helper', scriptKeysLookup, keyName) as RuleHelperBody).standalone

export const buildHelperKeysMeta = (scriptKeysLookup: ScriptKeysLookup, keyNames: string[]) =>
  keyNames
    .map(keyName => ({
      name: keyName,
      type: getDataType('helper', scriptKeysLookup, keyName),
      defaultValue: getDefaultValue(scriptKeysLookup, keyName, 'helper'),
      initiallyMockable: isMockableHelper(scriptKeysLookup, keyName),
    }))
    .sort((first, second) => first.name.localeCompare(second.name))

export const withoutStandaloneHelpersWithoutValue = (
  scriptKeysLookup: ScriptKeysLookup,
  helpers: string[],
  values: UsedValues
) =>
  helpers.filter(helper => {
    const isStandalone = helper in scriptKeysLookup.helpers && scriptKeysLookup.helpers[helper].standalone

    return !isStandalone || (isStandalone && helper in values.helpers)
  })
